/**
 * PublicCotationPage — Phase 2C.1 + Phase A (actions client)
 *
 * Page PUBLIQUE accessible via /cotation/<token> (sans auth).
 *
 * La clinique a 3 actions possibles :
 *   1. ✅ Valider la cotation (signature électronique + nom + rôle)
 *   2. ✏️ Demander une modification (nom + rôle + commentaire obligatoire +
 *      lignes souhaitées optionnelles)
 *   3. ❌ Rejeter la cotation (nom + rôle + commentaire obligatoire)
 *
 * Une fois une action effectuée, le client voit un récap read-only et ne
 * peut plus modifier (sauf si la cotation est en "modification_requested" ou
 * "rejected" et que la pharmacie n'a pas encore décidé — dans ces cas on
 * autorise la re-soumission, c'est l'admin qui tranche côté pharmacie).
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ClipboardCheck, Loader2, CheckCircle2, AlertCircle, Eraser,
  Pencil, XCircle, Plus, Trash2, MessageSquare,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const fmtFCFA = (n) => (n == null ? '—' : `${Math.round(n).toLocaleString('fr-FR')} FCFA`);

// ─── Canvas signature ──────────────────────────────────────────────────
const SignatureCanvas = ({ onChange }) => {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const last = useRef(null);
  const [isEmpty, setIsEmpty] = useState(true);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    const ctx = canvas.getContext('2d');
    ctx.scale(dpr, dpr);
    ctx.strokeStyle = '#1F2937';
    ctx.lineWidth = 2.2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, rect.width, rect.height);
  }, []);

  const getPos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    if (e.touches && e.touches[0]) return { x: e.touches[0].clientX - rect.left, y: e.touches[0].clientY - rect.top };
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };
  const start = (e) => { e.preventDefault(); drawing.current = true; last.current = getPos(e); };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const pos = getPos(e);
    ctx.beginPath();
    ctx.moveTo(last.current.x, last.current.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    last.current = pos;
    if (isEmpty) setIsEmpty(false);
  };
  const end = () => {
    if (!drawing.current) return;
    drawing.current = false;
    last.current = null;
    onChange?.(canvasRef.current.toDataURL('image/png'));
  };
  const clear = () => {
    const ctx = canvasRef.current.getContext('2d');
    const rect = canvasRef.current.getBoundingClientRect();
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, rect.width, rect.height);
    setIsEmpty(true);
    onChange?.(null);
  };

  return (
    <div className="space-y-2">
      <div className="relative border-2 border-dashed border-zinc-300 rounded-lg bg-white overflow-hidden" style={{ height: '180px' }}>
        <canvas ref={canvasRef}
          onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
          onTouchStart={start} onTouchMove={move} onTouchEnd={end}
          style={{ width: '100%', height: '100%', touchAction: 'none', cursor: 'crosshair' }}
          data-testid="cotation-signature-canvas"
        />
        {isEmpty && <p className="absolute inset-0 flex items-center justify-center text-zinc-300 italic text-sm pointer-events-none">✍️ Signez ici</p>}
      </div>
      <Button type="button" variant="outline" size="sm" onClick={clear} data-testid="cotation-signature-clear">
        <Eraser className="w-3.5 h-3.5 mr-1" /> Effacer
      </Button>
    </div>
  );
};

// ─── Référentiels de motifs (Phase A v2) ────────────────────────────────
const REVISION_REASON_OPTIONS = [
  { key: 'price_global_too_high', label: 'Montant global trop élevé' },
  { key: 'price_some_items', label: 'Certains produits sont trop chers' },
  { key: 'qty_to_reduce', label: 'Quantités à réduire' },
  { key: 'substitution', label: 'Demande de produit de substitution' },
  { key: 'not_needed', label: 'Produit non souhaité' },
  { key: 'need_clarification', label: 'Besoin de précision' },
  { key: 'other', label: 'Autre motif' },
];
const REVISION_LINE_TRIGGER_REASONS = new Set(['price_some_items', 'qty_to_reduce', 'substitution', 'not_needed']);

const REFUSAL_REASON_OPTIONS = [
  { key: 'price_global_too_high', label: 'Trop cher globalement' },
  { key: 'no_budget', label: 'Budget non disponible' },
  { key: 'order_cancelled', label: 'Commande annulée' },
  { key: 'bought_elsewhere', label: 'Achat effectué ailleurs' },
  { key: 'postponed', label: 'Besoin reporté' },
  { key: 'other', label: 'Autre' },
];

const LINE_ACTION_OPTIONS = [
  { value: 'keep', label: 'Accepter telle quelle' },
  { value: 'reduce_qty', label: 'Réduire la quantité' },
  { value: 'substitute', label: 'Demander un équivalent' },
  { value: 'remove', label: 'Supprimer cette ligne' },
];

const ReasonCheckboxGrid = ({ options, selected, onChange, testIdPrefix }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
    {options.map((opt) => {
      const checked = selected.includes(opt.key);
      return (
        <label
          key={opt.key}
          className={`flex items-center gap-2 px-3 py-2 rounded border cursor-pointer text-sm transition-colors ${
            checked ? 'border-emerald-500 bg-emerald-50 text-emerald-900' : 'border-zinc-200 bg-white hover:border-zinc-300 text-zinc-700'
          }`}
          data-testid={`${testIdPrefix}-${opt.key}`}
        >
          <input
            type="checkbox"
            checked={checked}
            onChange={(e) => {
              if (e.target.checked) onChange([...selected, opt.key]);
              else onChange(selected.filter((k) => k !== opt.key));
            }}
            className="w-4 h-4 accent-emerald-600"
          />
          <span>{opt.label}</span>
        </label>
      );
    })}
  </div>
);

// ─── Sous-formulaires ──────────────────────────────────────────────────

const IdentityFields = ({ name, role, onName, onRole, testIdPrefix }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
    <div>
      <Label className="text-xs font-medium text-zinc-600">Votre nom *</Label>
      <Input
        value={name}
        onChange={(e) => onName(e.target.value)}
        placeholder="Ex : Dr Konan"
        data-testid={`${testIdPrefix}-name`}
      />
    </div>
    <div>
      <Label className="text-xs font-medium text-zinc-600">Fonction (optionnel)</Label>
      <Input
        value={role}
        onChange={(e) => onRole(e.target.value)}
        placeholder="Ex : Directrice médicale"
        data-testid={`${testIdPrefix}-role`}
      />
    </div>
  </div>
);

const ValidateForm = ({ token, onDone }) => {
  const [sig, setSig] = useState(null);
  const [name, setName] = useState('');
  const [role, setRole] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!sig) { toast.error('Veuillez signer dans le cadre.'); return; }
    if (name.trim().length < 2) { toast.error('Nom requis (2 caractères minimum).'); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/b2b/public/cotations/${token}/validate`, {
        signature_png_base64: sig,
        signed_by_name: name.trim(),
        signed_by_role: role.trim() || null,
      });
      toast.success('Cotation validée · merci !');
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de la validation');
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-3" data-testid="cotation-form-validate">
      <p className="text-xs text-zinc-600">
        En signant, vous validez les prix, quantités et conditions de cette cotation.
      </p>
      <SignatureCanvas onChange={setSig} />
      <IdentityFields name={name} role={role} onName={setName} onRole={setRole} testIdPrefix="cotation-validate" />
      <Button onClick={submit} disabled={busy || !sig || name.trim().length < 2}
        className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
        data-testid="cotation-submit-validate">
        {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
        Confirmer la validation
      </Button>
    </div>
  );
};

const RevisionForm = ({ token, cot, onDone }) => {
  const [name, setName] = useState('');
  const [role, setRole] = useState('');
  const [comment, setComment] = useState('');
  const [reasons, setReasons] = useState([]);
  const [lineActions, setLineActions] = useState({}); // { [item_index]: {action, comment, requested_quantity, suggested_designation} }
  const [items, setItems] = useState([{ designation: '', quantity: '', observation: '' }]);
  const [busy, setBusy] = useState(false);

  const showLinePicker = reasons.some((r) => REVISION_LINE_TRIGGER_REASONS.has(r));
  const cotItems = cot.items || [];

  const updateItem = (i, k, v) => setItems((a) => a.map((it, idx) => (idx === i ? { ...it, [k]: v } : it)));
  const addItem = () => setItems((a) => [...a, { designation: '', quantity: '', observation: '' }]);
  const delItem = (i) => setItems((a) => a.filter((_, idx) => idx !== i));

  const setLineAction = (idx, patch) => {
    setLineActions((prev) => {
      const next = { ...prev };
      if (patch.action == null || patch.action === '') {
        delete next[idx];
      } else {
        next[idx] = { ...(next[idx] || {}), ...patch };
      }
      return next;
    });
  };

  const submit = async () => {
    if (name.trim().length < 2) { toast.error('Nom requis (2 caractères minimum).'); return; }
    if (comment.trim().length < 5) { toast.error('Commentaire requis (5 caractères minimum).'); return; }
    setBusy(true);
    try {
      const cleanReqItems = items
        .map((it) => ({
          designation: (it.designation || '').trim(),
          quantity: it.quantity === '' ? null : Number(it.quantity),
          observation: (it.observation || '').trim() || null,
        }))
        .filter((it) => it.designation.length > 0);
      const cleanLineActions = Object.entries(lineActions)
        .filter(([, v]) => v && v.action)
        .map(([k, v]) => ({
          item_index: Number(k),
          action: v.action,
          comment: (v.comment || '').trim() || null,
          requested_quantity: v.requested_quantity === '' || v.requested_quantity == null ? null : Number(v.requested_quantity),
          suggested_designation: (v.suggested_designation || '').trim() || null,
        }));
      await axios.post(`${API}/b2b/public/cotations/${token}/request-modification`, {
        by_name: name.trim(),
        by_role: role.trim() || null,
        comment: comment.trim(),
        reasons,
        requested_items: cleanReqItems,
        line_actions: cleanLineActions,
      });
      toast.success('Demande de révision envoyée');
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="cotation-form-revision">
      <IdentityFields name={name} role={role} onName={setName} onRole={setRole} testIdPrefix="cotation-mod" />

      <div>
        <Label className="text-xs font-medium text-zinc-600 mb-1.5 block">Motifs de la révision (au moins un recommandé)</Label>
        <ReasonCheckboxGrid options={REVISION_REASON_OPTIONS} selected={reasons} onChange={setReasons} testIdPrefix="rev-reason" />
      </div>

      {showLinePicker && cotItems.length > 0 && (
        <div data-testid="rev-line-picker">
          <Label className="text-xs font-medium text-zinc-600 mb-1.5 block">
            Lignes concernées ({cotItems.length} produit{cotItems.length > 1 ? 's' : ''}) — laissez vide si pas de souci
          </Label>
          <div className="space-y-2 max-h-72 overflow-y-auto border border-zinc-200 rounded p-2 bg-zinc-50">
            {cotItems.map((it, i) => {
              const a = lineActions[i] || {};
              return (
                <div key={i} className="bg-white rounded border border-zinc-200 p-2" data-testid={`rev-line-${i}`}>
                  <p className="text-xs font-medium text-zinc-900 truncate">{it.designation}</p>
                  <p className="text-[10px] text-zinc-500">Qté {it.quantity}{it.unit ? ` ${it.unit}` : ''} · {fmtFCFA((it.quantity || 0) * (it.unit_price || 0))}</p>
                  <div className="mt-1.5 flex flex-col sm:flex-row gap-1.5">
                    <select
                      value={a.action || ''}
                      onChange={(e) => setLineAction(i, { action: e.target.value || null })}
                      className="text-xs h-7 px-1.5 border border-zinc-200 rounded bg-white focus:border-emerald-400 focus:outline-none flex-1"
                      data-testid={`rev-line-${i}-action`}
                    >
                      <option value="">— Aucune action —</option>
                      {LINE_ACTION_OPTIONS.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                    </select>
                    {a.action === 'reduce_qty' && (
                      <Input
                        type="number"
                        min="0"
                        placeholder="Nouvelle qté"
                        value={a.requested_quantity || ''}
                        onChange={(e) => setLineAction(i, { requested_quantity: e.target.value })}
                        className="h-7 text-xs sm:w-32"
                        data-testid={`rev-line-${i}-qty`}
                      />
                    )}
                    {a.action === 'substitute' && (
                      <Input
                        placeholder="Équivalent souhaité"
                        value={a.suggested_designation || ''}
                        onChange={(e) => setLineAction(i, { suggested_designation: e.target.value })}
                        className="h-7 text-xs flex-1"
                        data-testid={`rev-line-${i}-sub`}
                      />
                    )}
                  </div>
                  {a.action && (
                    <Input
                      placeholder="Commentaire (optionnel)"
                      value={a.comment || ''}
                      onChange={(e) => setLineAction(i, { comment: e.target.value })}
                      className="h-7 text-xs mt-1.5"
                      data-testid={`rev-line-${i}-comment`}
                    />
                  )}
                </div>
              );
            })}
          </div>
          <p className="text-[10px] text-zinc-500 italic mt-1">
            Astuce : sélectionnez « Aucune action » pour les lignes qui vous conviennent — pas besoin de tout commenter.
          </p>
        </div>
      )}

      <div>
        <Label className="text-xs font-medium text-zinc-600">Commentaire * (précisez votre demande)</Label>
        <Textarea
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          rows={3}
          placeholder="Merci de préciser votre demande afin que nous puissions préparer une cotation révisée."
          data-testid="cotation-mod-comment"
          maxLength={4000}
        />
      </div>

      <div>
        <div className="flex items-center justify-between mb-1.5">
          <Label className="text-xs font-medium text-zinc-600">
            Produits supplémentaires souhaités (optionnel)
          </Label>
          <Button type="button" variant="outline" size="sm" onClick={addItem} className="h-7 text-xs" data-testid="cotation-mod-add-line">
            <Plus className="w-3 h-3 mr-1" /> Ligne
          </Button>
        </div>
        <div className="space-y-1.5">
          {items.map((it, i) => (
            <div key={i} className="grid grid-cols-12 gap-1.5 items-start">
              <Input
                className="col-span-6 text-xs h-8"
                placeholder="Désignation produit"
                value={it.designation}
                onChange={(e) => updateItem(i, 'designation', e.target.value)}
                data-testid={`cotation-mod-item-${i}-designation`}
              />
              <Input
                className="col-span-2 text-xs h-8"
                placeholder="Qté"
                type="number"
                min="0"
                value={it.quantity}
                onChange={(e) => updateItem(i, 'quantity', e.target.value)}
                data-testid={`cotation-mod-item-${i}-qty`}
              />
              <Input
                className="col-span-3 text-xs h-8"
                placeholder="Observation"
                value={it.observation}
                onChange={(e) => updateItem(i, 'observation', e.target.value)}
                data-testid={`cotation-mod-item-${i}-obs`}
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="col-span-1 h-7 w-7"
                onClick={() => delItem(i)}
                disabled={items.length === 1}
                data-testid={`cotation-mod-item-${i}-del`}
              >
                <Trash2 className="w-3 h-3 text-red-500" />
              </Button>
            </div>
          ))}
        </div>
      </div>

      <Button
        onClick={submit}
        disabled={busy || name.trim().length < 2 || comment.trim().length < 5}
        className="w-full bg-amber-600 hover:bg-amber-700 text-white"
        data-testid="cotation-submit-modification"
      >
        {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Pencil className="w-4 h-4 mr-2" />}
        Envoyer la demande de révision
      </Button>
    </div>
  );
};

const RejectForm = ({ token, onDone }) => {
  const [name, setName] = useState('');
  const [role, setRole] = useState('');
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (name.trim().length < 2) { toast.error('Nom requis (2 caractères minimum).'); return; }
    if (comment.trim().length < 5) { toast.error('Commentaire requis (5 caractères minimum).'); return; }
    if (!window.confirm('Confirmez-vous le rejet de cette cotation ? Cette action notifiera la pharmacie.')) return;
    setBusy(true);
    try {
      await axios.post(`${API}/b2b/public/cotations/${token}/reject`, {
        by_name: name.trim(),
        by_role: role.trim() || null,
        comment: comment.trim(),
      });
      toast.success('Cotation rejetée · la pharmacie sera notifiée');
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors du rejet');
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-3" data-testid="cotation-form-reject">
      <IdentityFields name={name} role={role} onName={setName} onRole={setRole} testIdPrefix="cotation-rej" />
      <div>
        <Label className="text-xs font-medium text-zinc-600">Motif du rejet *</Label>
        <Textarea
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          rows={3}
          placeholder="Ex : Prix trop élevés / Délai non compatible / Erreur dans la commande..."
          data-testid="cotation-rej-comment"
          maxLength={4000}
        />
      </div>
      <Button
        onClick={submit}
        disabled={busy || name.trim().length < 2 || comment.trim().length < 5}
        variant="destructive"
        className="w-full"
        data-testid="cotation-submit-reject"
      >
        {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <XCircle className="w-4 h-4 mr-2" />}
        Rejeter la cotation
      </Button>
    </div>
  );
};

// ─── Page principale ───────────────────────────────────────────────────

const PublicCotationPage = ({ token }) => {
  const [cot, setCot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [mode, setMode] = useState(null); // null | 'validate' | 'modify' | 'reject'

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const r = await axios.get(`${API}/b2b/public/cotations/${token}`);
      setCot(r.data);
      setMode(null);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Lien invalide ou expiré.');
    }
    setLoading(false);
  }, [token]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 to-white"><Loader2 className="w-8 h-8 animate-spin text-emerald-600" /></div>;
  }
  if (err) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-red-50 to-white p-4">
        <div className="max-w-md bg-white rounded-xl shadow-lg p-6 text-center">
          <AlertCircle className="w-12 h-12 mx-auto text-red-500 mb-3" />
          <h1 className="text-xl font-bold text-zinc-900 mb-2">Lien invalide</h1>
          <p className="text-sm text-zinc-600">{err}</p>
          <p className="text-xs text-zinc-400 mt-4">Pharmacie Saint Ange Des Amis · Cocody Angré</p>
        </div>
      </div>
    );
  }

  const items = cot.items || [];
  const status = cot.cotation_validation_status;
  const isValidated = status === 'validated';
  const isRejected = status === 'rejected';
  const isModRequested = status === 'modification_requested';
  const isFinalized = isValidated || isRejected || isModRequested;

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-zinc-50 py-6 px-3" data-testid="public-cotation-page">
      <div className="max-w-2xl mx-auto space-y-4">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-md p-5">
          <p className="text-xs text-zinc-500 uppercase tracking-wide">Pharmacie Saint Ange Des Amis</p>
          <h1 className="text-2xl font-bold text-zinc-900 mt-0.5 flex items-center gap-2">
            <ClipboardCheck className="w-6 h-6 text-emerald-600" />
            Cotation à valider
          </h1>
          <p className="text-sm text-zinc-600 mt-1 font-mono">
            Dossier #{cot.id?.slice(0, 8).toUpperCase()}
            {cot.created_at && ` · ${new Date(cot.created_at).toLocaleDateString('fr-FR')}`}
          </p>
          <p className="text-sm text-zinc-700 mt-2">
            Destinataire : <span className="font-semibold">{cot.clinic_name || '—'}</span>
          </p>
        </div>

        {/* Détail */}
        <div className="bg-white rounded-xl shadow-md p-5">
          <h2 className="font-semibold text-zinc-900 mb-3">Détail de la cotation</h2>
          <div className="overflow-x-auto -mx-2">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-zinc-500 border-b border-zinc-200">
                  <th className="text-left px-2 py-2">Désignation</th>
                  <th className="text-right px-2 py-2">Qté</th>
                  <th className="text-right px-2 py-2">PU TTC</th>
                  <th className="text-right px-2 py-2">Total</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it, i) => (
                  <tr key={i} className="border-b border-zinc-100">
                    <td className="px-2 py-2">
                      <p className="text-zinc-900">{it.designation}</p>
                      {it.code && <p className="text-[10px] text-zinc-400 font-mono">code {it.code}</p>}
                      <p
                        className="text-[11px] text-emerald-700 mt-0.5 inline-flex items-center gap-1"
                        data-testid={`cotation-delay-${i}`}
                      >
                        <span aria-hidden="true">⏱</span>
                        <span>Délai : <strong>{it.delivery_delay || '48h après validation'}</strong></span>
                      </p>
                    </td>
                    <td className="px-2 py-2 text-right align-top">{it.quantity}{it.unit ? ` ${it.unit}` : ''}</td>
                    <td className="px-2 py-2 text-right text-zinc-600 align-top">{fmtFCFA(it.unit_price)}</td>
                    <td className="px-2 py-2 text-right font-medium align-top">{fmtFCFA(it.total_ttc != null ? it.total_ttc : (it.quantity || 0) * (it.unit_price || 0))}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="font-bold text-base bg-amber-50">
                  <td colSpan="3" className="px-2 py-2.5 text-right">TOTAL TTC</td>
                  <td className="px-2 py-2.5 text-right text-emerald-700">{fmtFCFA(cot.total_amount)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
          <p className="text-[11px] text-zinc-500 mt-2 italic">
            Les délais affichés débutent à la validation de la cotation, sauf mention contraire.
          </p>
          {cot.comment && (
            <div className="mt-3 p-3 bg-zinc-50 rounded text-sm text-zinc-700">
              <span className="font-semibold">Note :</span> {cot.comment}
            </div>
          )}
        </div>

        {/* ─── États finalisés (validation / rejet / modif demandée) ── */}
        {isValidated && (
          <div className="bg-emerald-50 border-2 border-emerald-300 rounded-xl shadow-md p-5" data-testid="cotation-state-validated">
            <div className="flex items-center gap-2 text-emerald-800 mb-3">
              <CheckCircle2 className="w-6 h-6" />
              <h2 className="font-semibold">Cotation validée</h2>
            </div>
            <p className="text-sm text-emerald-900">
              <strong>{cot.cotation_validated_by_name}</strong>
              {cot.cotation_validated_by_role ? ` (${cot.cotation_validated_by_role})` : ''}
            </p>
            <p className="text-xs text-emerald-700 mt-1">
              Validé le {new Date(cot.cotation_validated_at).toLocaleString('fr-FR', { dateStyle: 'long', timeStyle: 'short' })}
            </p>
            {cot.cotation_signature_png_base64 && (
              <img src={cot.cotation_signature_png_base64} alt="Signature" className="mt-3 max-w-[280px] bg-white border border-emerald-200 rounded" data-testid="cotation-signature-img" />
            )}
            <p className="text-xs text-zinc-500 mt-4 italic">
              La pharmacie a été notifiée et procédera à la facturation. Vous recevrez ensuite le Bon de livraison.
            </p>
          </div>
        )}

        {isRejected && cot.cotation_rejection && (
          <div className="bg-red-50 border-2 border-red-300 rounded-xl shadow-md p-5" data-testid="cotation-state-rejected">
            <div className="flex items-center gap-2 text-red-800 mb-3">
              <XCircle className="w-6 h-6" />
              <h2 className="font-semibold">Cotation rejetée</h2>
            </div>
            <p className="text-sm text-red-900">
              Par <strong>{cot.cotation_rejection.by_name}</strong>
              {cot.cotation_rejection.by_role ? ` (${cot.cotation_rejection.by_role})` : ''}
            </p>
            <p className="text-xs text-red-700 mt-1">
              Le {new Date(cot.cotation_rejection.at).toLocaleString('fr-FR', { dateStyle: 'long', timeStyle: 'short' })}
            </p>
            <div className="mt-2 p-3 bg-white border border-red-200 rounded text-sm text-zinc-800 whitespace-pre-wrap">
              {cot.cotation_rejection.comment}
            </div>
            <p className="text-xs text-zinc-500 mt-4 italic">
              La pharmacie a été notifiée. Elle prendra contact pour la suite.
            </p>
          </div>
        )}

        {isModRequested && cot.cotation_modification_request && (
          <div className="bg-amber-50 border-2 border-amber-300 rounded-xl shadow-md p-5" data-testid="cotation-state-modification">
            <div className="flex items-center gap-2 text-amber-900 mb-3">
              <Pencil className="w-6 h-6" />
              <h2 className="font-semibold">Modification demandée</h2>
            </div>
            <p className="text-sm text-amber-900">
              Par <strong>{cot.cotation_modification_request.by_name}</strong>
              {cot.cotation_modification_request.by_role ? ` (${cot.cotation_modification_request.by_role})` : ''}
            </p>
            <p className="text-xs text-amber-700 mt-1">
              Le {new Date(cot.cotation_modification_request.at).toLocaleString('fr-FR', { dateStyle: 'long', timeStyle: 'short' })}
            </p>
            <div className="mt-2 p-3 bg-white border border-amber-200 rounded text-sm text-zinc-800 whitespace-pre-wrap">
              {cot.cotation_modification_request.comment}
            </div>
            {cot.cotation_modification_request.requested_items?.length > 0 && (
              <div className="mt-2">
                <p className="text-xs font-semibold text-amber-900 mb-1">Lignes souhaitées :</p>
                <ul className="text-xs text-zinc-700 space-y-0.5 list-disc list-inside">
                  {cot.cotation_modification_request.requested_items.map((it, i) => (
                    <li key={i}>
                      <strong>{it.designation}</strong>
                      {it.quantity != null && ` · qté ${it.quantity}`}
                      {it.observation && ` — ${it.observation}`}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <p className="text-xs text-zinc-500 mt-4 italic">
              La pharmacie va préparer une nouvelle cotation et vous l'enverra prochainement.
            </p>
          </div>
        )}

        {/* ─── Actions disponibles si cotation non finalisée ─────────── */}
        {!isFinalized && !mode && (
          <div className="bg-white rounded-xl shadow-md p-5 border-2 border-zinc-200" data-testid="cotation-actions">
            <h2 className="font-semibold text-zinc-900 mb-3">Que souhaitez-vous faire ?</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <Button
                onClick={() => setMode('validate')}
                className="bg-emerald-600 hover:bg-emerald-700 text-white h-auto py-3 flex-col gap-1"
                data-testid="cotation-action-validate"
              >
                <CheckCircle2 className="w-5 h-5" />
                <span className="text-sm font-semibold">Valider</span>
                <span className="text-[10px] opacity-90">Signature + nom</span>
              </Button>
              <Button
                onClick={() => setMode('modify')}
                className="bg-amber-600 hover:bg-amber-700 text-white h-auto py-3 flex-col gap-1"
                data-testid="cotation-action-modify"
              >
                <Pencil className="w-5 h-5" />
                <span className="text-sm font-semibold">Modifier</span>
                <span className="text-[10px] opacity-90">Demande de changement</span>
              </Button>
              <Button
                onClick={() => setMode('reject')}
                variant="destructive"
                className="h-auto py-3 flex-col gap-1"
                data-testid="cotation-action-reject"
              >
                <XCircle className="w-5 h-5" />
                <span className="text-sm font-semibold">Rejeter</span>
                <span className="text-[10px] opacity-90">Avec motif obligatoire</span>
              </Button>
            </div>
            <p className="text-xs text-zinc-500 mt-3 flex items-start gap-1.5">
              <MessageSquare className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              Pour toute action, votre nom est obligatoire. La pharmacie sera notifiée et conservera l'historique de votre choix.
            </p>
          </div>
        )}

        {!isFinalized && mode && (
          <div className="bg-white rounded-xl shadow-md p-5 border-2 border-zinc-200">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-semibold text-zinc-900">
                {mode === 'validate' && '✅ Valider la cotation'}
                {mode === 'modify' && '✏️ Demander une modification'}
                {mode === 'reject' && '❌ Rejeter la cotation'}
              </h2>
              <Button variant="ghost" size="sm" onClick={() => setMode(null)} data-testid="cotation-back-to-actions">
                ← Retour
              </Button>
            </div>
            {mode === 'validate' && <ValidateForm token={token} onDone={load} />}
            {mode === 'modify' && <RevisionForm token={token} cot={cot} onDone={load} />}
            {mode === 'reject' && <RejectForm token={token} onDone={load} />}
          </div>
        )}

        <p className="text-center text-xs text-zinc-400 pt-2">
          Pharmacie Saint Ange Des Amis · Cocody Angré · 07 78 41 52 77
        </p>
      </div>
    </div>
  );
};

export default PublicCotationPage;
